import type { TableColumnDetails } from './types';

export const ASSETS_PER_PAGE = 100;
export const ASSET_TABLE_COLUMNS_STORAGE_KEY = 'asset-administration.table';
export const DEVICE_SHARINGS_FEATURE_TOGGLE_KEY = 'asset-administration.device-sharings.enabled';
const TYPE_COLUMN_KEY = 'type';
const VEHICLE_COLUMN_KEY = 'vehicle';
const MIN_COLUMN_WIDTHS: Partial<Record<string, number>> = {
    [VEHICLE_COLUMN_KEY]: 280,
};

export const getAssetTableColumnsStorageKey = (accountId: string | null | undefined) => {
    const accountPrefix = accountId?.slice(0, 6) ?? 'default';

    return `${ASSET_TABLE_COLUMNS_STORAGE_KEY}:${accountPrefix}`;
};

export const isDeviceSharingsFeatureEnabled = () =>
    typeof localStorage !== 'undefined' && localStorage.getItem(DEVICE_SHARINGS_FEATURE_TOGGLE_KEY) === 'true';

export const getDefaultAssetTableColumns = (): TableColumnDetails[] => [
    {
        key: VEHICLE_COLUMN_KEY,
        visible: true,
        width: MIN_COLUMN_WIDTHS[VEHICLE_COLUMN_KEY],
        label: 'intl-msg:asset.type',
        sortable: true,
    },
    {
        key: TYPE_COLUMN_KEY,
        visible: false,
        width: 120,
        label: 'intl-msg:asset.type',
        sortable: true,
    },
    {
        key: 'name',
        visible: false,
        width: 280,
        label: 'intl-msg:asset.name',
        sortable: true,
    },
    {
        key: 'identification',
        visible: false,
        width: 200,
        label: 'intl-msg:asset.identification_type.vin',
        sortable: true,
    },
    {
        key: 'license_plate',
        visible: true,
        width: 160,
        label: 'intl-msg:asset.license-plate',
        sortable: true,
    },
    {
        key: 'brand',
        visible: true,
        width: 150,
        label: 'intl-msg:asset.brand',
        sortable: true,
    },
    {
        key: 'fuel_type',
        visible: true,
        width: 170,
        label: 'intl-msg:masterdata.fuel-type',
        sortable: true,
    },
    {
        key: 'status',
        visible: true,
        width: 90,
        label: 'intl-msg:asset.status',
        sortable: true,
    },
    {
        key: 'tags',
        visible: true,
        width: 200,
        label: 'intl-msg:asset.tags',
        sortable: false,
    },
    {
        key: 'devices',
        visible: true,
        label: 'intl-msg:asset.devices',
        sortable: false,
    },
    {
        key: 'sharings',
        visible: false,
        width: 180,
        label: 'intl-msg:sharing',
        sortable: false,
    },
];

export const isAssetTableColumnSortable = (
    columnKey: string,
    columns: TableColumnDetails[] = getDefaultAssetTableColumns()
): boolean => {
    return columns.find(column => column.key === columnKey)?.sortable ?? false;
};

export const normalizeAssetTableColumns = (storedColumns: TableColumnDetails[] | undefined): TableColumnDetails[] => {
    const defaultColumns = getDefaultAssetTableColumns();

    if (!storedColumns?.length) {
        return defaultColumns;
    }

    const defaultColumnsByKey = new Map(defaultColumns.map(column => [column.key, column]));
    const hasVehicleColumn = storedColumns.some(column => column.key === VEHICLE_COLUMN_KEY);
    const normalizedColumns: TableColumnDetails[] = storedColumns.flatMap(storedColumn => {
        const normalizedKey =
            storedColumn.key === TYPE_COLUMN_KEY && !hasVehicleColumn ? VEHICLE_COLUMN_KEY : storedColumn.key;
        const defaultColumn = defaultColumnsByKey.get(normalizedKey);

        if (!defaultColumn) {
            return [];
        }

        return [
            {
                ...defaultColumn,
                visible: storedColumn.visible,
                width:
                    typeof storedColumn.width === 'number' && typeof MIN_COLUMN_WIDTHS[normalizedKey] === 'number'
                        ? Math.max(storedColumn.width, MIN_COLUMN_WIDTHS[normalizedKey] as number)
                        : (storedColumn.width ?? defaultColumn.width),
            },
        ];
    });

    const columnsWithMissingDefaults: TableColumnDetails[] = [...normalizedColumns];

    for (const defaultColumn of defaultColumns) {
        if (columnsWithMissingDefaults.some(column => column.key === defaultColumn.key)) {
            continue;
        }

        const precedingDefaultKeys = defaultColumns
            .slice(
                0,
                defaultColumns.findIndex(column => column.key === defaultColumn.key)
            )
            .map(column => column.key);
        const insertionIndex = [...columnsWithMissingDefaults]
            .reverse()
            .findIndex(column => precedingDefaultKeys.includes(column.key));

        if (insertionIndex === -1) {
            columnsWithMissingDefaults.unshift(defaultColumn);
            continue;
        }

        const targetIndex = columnsWithMissingDefaults.length - insertionIndex;
        columnsWithMissingDefaults.splice(targetIndex, 0, defaultColumn);
    }

    return columnsWithMissingDefaults;
};
